/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';
// import { useManualGqlFetch } from '@search/gql-client/src';

import { AddScriptError } from '@search/classified-common/src/addScript';
import {
    CalltouchError,
    calltouchReplacementFetch,
    ReplaceError
} from '../../../../newbuilding/hooks/useCalltouchReplacement';
import { useClassifiedOfferDataLayerPush } from '../../../analytics/ClassifiedOfferGtmContext';
import { useClickerTracker } from '../../../../../src/services/clickerTracker/useClickerTracker';
import { useOfferPhoneWithCaptcha } from './captcha';

import {
    OfferPhoneQuery$data as OfferPhoneQueryResponse,
    OfferPhoneQuery$variables as OfferPhoneQueryVariables
} from './__generated__/OfferPhoneQuery.graphql';
// import { OfferPhoneQuery } from './OfferPhoneQuery';

export function useOfferPhoneFetch() {
    const phoneQueryFetch = useOfferPhoneWithCaptcha();
    // const phoneQueryFetch = useManualGqlFetch<OfferPhoneQueryVariables, OfferPhoneQueryResponse>(OfferPhoneQuery, { pathUniqSuffix: '/getPhone', extraHeaders: { 'Captcha-Token': 'token' } });
    const dataLayerPush = useClassifiedOfferDataLayerPush();
    const { trackPhoneView } = useClickerTracker();

    return React.useCallback(async (variables: OfferPhoneQueryVariables & { callTouchId?: string }) => {
        const orig = await phoneQueryFetch(variables);
        const phone = orig.offerPhone?.phone!;
        let calltouchPhone: string | undefined;

        try {
            calltouchPhone = phone?.formattedNumber && variables.callTouchId ?
                await calltouchReplacementFetch({
                    callTouchId: variables.callTouchId,
                    originalPhone: phone.formattedNumber
                }) :
                undefined;
            if (calltouchPhone) {
                dataLayerPush({
                    event: 'cl_phn_replace',
                    nb_phone: phone.formattedNumber,
                    replaceable: phone.isReplaceable,
                    replaced: Boolean(calltouchPhone),
                    ct_replace_result: calltouchPhone
                });
            }
        } catch (error: any) {
            // eslint-disable-next-line no-console
            console.error(error);

            let replaceError: ReplaceError = 'UNKNOWN';

            if (error instanceof AddScriptError) replaceError = 'SCRIPT';
            if (error instanceof CalltouchError) replaceError = error.code;

            dataLayerPush({
                event: 'cl_phn_replace_fail',
                nb_phone: phone.formattedNumber,
                replaceable: phone.isReplaceable,
                replaced: Boolean(calltouchPhone),
                'replace-error': replaceError,
                ct_replace_result: calltouchPhone
            });
        }

        trackPhoneView(variables.offerId, phone.countryCode, phone.number);

        return {
            ...phone,
            calltouchPhone,
            originalPhone: phone.formattedNumber,
            phoneFormatted: calltouchPhone ?? phone.formattedNumber,
            href: calltouchPhone ? `tel:${calltouchPhone.replace(/[^\d,\+]/g, '')}` : phone.href
        };
    }, [ dataLayerPush, phoneQueryFetch ]);
}

export function useOfferPhoneQuery({
    callTouchId,
    isCard
}: {
    isCard?: boolean;
    callTouchId?: string;
} = {}) {
    const phoneQueryFetch = useOfferPhoneFetch();
    const [ state, setState ] = React.useState({
        loading: false,
        error: undefined as Error | undefined,
        phoneFormatted: undefined as undefined | string,
        phoneHref: undefined as undefined | string,
        isDoppelganger: false,
        countryCode: undefined as undefined | string,
        number: undefined as undefined | string
    });

    const load = React.useCallback(async (variables: OfferPhoneQueryVariables) => {
        try {
            setState(prev => ({ ...prev, loading: true }));
            const phone = await phoneQueryFetch({ ...variables, isCard, callTouchId });

            setState({
                loading: false,
                error: undefined,
                phoneFormatted: phone.phoneFormatted,
                phoneHref: phone.href,
                isDoppelganger: phone.isDoppelganger ?? false,
                countryCode: phone.countryCode,
                number: phone.number
            });
        } catch (error) {
            setState(prev => ({ ...prev, loading: false, error }));
            // eslint-disable-next-line no-console
            console.error(error);
        }
    }, [ phoneQueryFetch, callTouchId ]);

    return React.useMemo(() => ({ ...state, load }), [ state, load ]);
}
